/**
 * Race Results Component
 * 
 * Displays all race events and their results
 * - Lists race events grouped by season
 * - Shows leaderboard for selected event
 * - Admin/coach can record new results
 * - Coach and admin access only
 * 
 * Requirements: 3.1-3.6, 7.1-7.6, 8.1-8.7, 12.1-12.5
 */

import { useEffect, useState, useCallback } from 'react';
import { restClient, Event, Rider } from '../../../api/rest-client';
import { RaceResult } from '../../../types/raceResults';
import { useAuth } from '../../../auth/AuthProvider';
import { Card } from '../../common/Card';
import { LoadingSpinner } from '../../common/LoadingSpinner';
import { useUIStore } from '../../../store/uiStore';
import { RaceResultLeaderboard } from './RaceResultLeaderboard';
import { RaceResultModal } from './RaceResultModal';
import { format } from 'date-fns';

interface GroupedEvents {
  [season: string]: Event[];
}

export function RaceResults() {
  const { isAdmin, isCoach } = useAuth();
  const showToast = useUIStore(state => state.showToast);
  const [events, setEvents] = useState<Event[]>([]);
  const [groupedEvents, setGroupedEvents] = useState<GroupedEvents>({});
  const [selectedEventId, setSelectedEventId] = useState<string | null>(null);
  const [selectedEvent, setSelectedEvent] = useState<Event | null>(null);
  const [currentSeason, setCurrentSeason] = useState<string>('');
  const [seasonFilter, setSeasonFilter] = useState<string>('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showRecordModal, setShowRecordModal] = useState(false);
  const [editingResult, setEditingResult] = useState<RaceResult | null>(null);
  const [raceResults, setRaceResults] = useState<RaceResult[]>([]);
  const [riders, setRiders] = useState<Rider[]>([]);
  const [loadingResults, setLoadingResults] = useState(false);

  /**
   * Fetch current season and events
   */
  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);

      // Get current season
      const seasonConfig = await restClient.getSeasonConfig();
      setCurrentSeason(seasonConfig.currentSeason);
      
      // Set season filter to current season if not already set
      if (!seasonFilter) {
        setSeasonFilter(seasonConfig.currentSeason);
      }

      // Get all events
      const allEvents = await restClient.getEvents();
      
      console.log('[RaceResults] Total events:', allEvents.length);
      console.log('[RaceResults] Event types:', [...new Set(allEvents.map(e => e.event_type))]);
      
      // Filter to race events only (case-insensitive)
      const raceEvents = allEvents.filter(event => {
        const eventType = event.event_type.toLowerCase();
        return eventType === 'race' || eventType.includes('race');
      });

      console.log('[RaceResults] Race events found:', raceEvents.length);

      // Sort by date (most recent first)
      raceEvents.sort((a, b) => 
        new Date(b.event_starttime).getTime() - new Date(a.event_starttime).getTime()
      );

      setEvents(raceEvents);

      // Group events by season
      const grouped: GroupedEvents = {};
      raceEvents.forEach(event => {
        const season = event.season || new Date(event.event_starttime).getFullYear().toString();
        if (!grouped[season]) {
          grouped[season] = [];
        }
        grouped[season].push(event);
      });

      setGroupedEvents(grouped);
      console.log('[RaceResults] Grouped by season:', Object.keys(grouped));

      // Auto-select first event if none selected
      if (!selectedEventId && raceEvents.length > 0) {
        const firstEvent = raceEvents[0];
        setSelectedEventId(firstEvent.event_id);
        setSelectedEvent(firstEvent);
      }

      // Fetch riders for name lookup
      const ridersList = await restClient.getRiders();
      setRiders(ridersList);
    } catch (err) {
      console.error('Failed to fetch race events:', err);
      setError('Failed to load race events. Please try again.');
      showToast('error', 'Failed to load race events');
    } finally {
      setLoading(false);
    }
  }, [selectedEventId, showToast]);

  /**
   * Fetch race results for selected event
   */
  const fetchRaceResults = useCallback(async (eventId: string) => {
    try {
      setLoadingResults(true);
      const results = await restClient.getRaceResults({ event_id: eventId });
      setRaceResults(results);
    } catch (err) {
      console.error('Failed to fetch race results:', err);
      showToast('error', 'Failed to load race results');
      setRaceResults([]);
    } finally {
      setLoadingResults(false);
    }
  }, [showToast]);

  /**
   * Handle event selection
   */
  const handleSelectEvent = useCallback((event: Event) => {
    setSelectedEventId(event.event_id);
    setSelectedEvent(event);
    fetchRaceResults(event.event_id);
  }, [fetchRaceResults]);

  /**
   * Handle season filter change
   */
  const handleSeasonFilterChange = useCallback((e: React.ChangeEvent<HTMLSelectElement>) => {
    setSeasonFilter(e.target.value);
  }, []);

  /**
   * Handle open record modal
   */
  const handleOpenRecordModal = useCallback(() => {
    setEditingResult(null);
    setShowRecordModal(true);
  }, []);

  /**
   * Handle open edit modal
   */
  const handleOpenEditModal = useCallback((result: RaceResult) => {
    setEditingResult(result);
    setShowRecordModal(true);
  }, []);

  /**
   * Handle close record modal
   */
  const handleCloseRecordModal = useCallback(() => {
    setShowRecordModal(false);
    setEditingResult(null);
  }, []);

  /**
   * Handle successful race result recording
   */
  const handleRecordSuccess = useCallback(() => {
    // Refresh results to show new/updated result
    if (selectedEventId) {
      fetchRaceResults(selectedEventId);
    }
  }, [selectedEventId, fetchRaceResults]);

  /**
   * Get filtered events based on season filter
   */
  const getFilteredEvents = useCallback((): Event[] => {
    if (seasonFilter === 'all' || !seasonFilter) {
      return events;
    }
    return events.filter(event => {
      const eventSeason = event.season || new Date(event.event_starttime).getFullYear().toString();
      return eventSeason === seasonFilter;
    });
  }, [events, seasonFilter]);

  /**
   * Get available seasons for filter
   */
  const getAvailableSeasons = useCallback((): string[] => {
    const seasons = Object.keys(groupedEvents).sort((a, b) => parseInt(b) - parseInt(a));
    return seasons;
  }, [groupedEvents]);

  // Fetch data on mount
  useEffect(() => {
    fetchData();
  }, [fetchData]);

  // Fetch results when event is selected
  useEffect(() => {
    if (selectedEventId) {
      fetchRaceResults(selectedEventId);
    }
  }, [selectedEventId, fetchRaceResults]);

  if (loading) {
    return (
      <div className="flex justify-center items-center py-12">
        <LoadingSpinner size="lg" />
      </div>
    );
  }

  if (error) {
    return (
      <Card>
        <div className="text-center py-8">
          <p className="text-red-600 mb-4">{error}</p>
          <button
            onClick={fetchData}
            className="px-4 py-2 bg-primary-500 text-white rounded-md hover:bg-primary-600"
          >
            Try Again
          </button>
        </div>
      </Card>
    );
  }

  const filteredEvents = getFilteredEvents();
  const availableSeasons = getAvailableSeasons();

  return (
    <div className="space-y-6">
      {/* Page Header */}
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Race Results</h1>
          <p className="text-gray-600 mt-1">
            View and manage race results
          </p>
        </div>
      </div>

      {/* Season Filter */}
      {availableSeasons.length > 0 && (
        <Card>
          <div className="flex items-center space-x-4">
            <label htmlFor="season-filter" className="text-sm font-medium text-gray-700">
              Season:
            </label>
            <select
              id="season-filter"
              value={seasonFilter}
              onChange={handleSeasonFilterChange}
              className="px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500"
            >
              <option value="all">All Seasons</option>
              {availableSeasons.map(season => (
                <option key={season} value={season}>
                  {season}
                  {season === currentSeason && ' (Current)'}
                </option>
              ))}
            </select>
            <span className="text-sm text-gray-500">
              {filteredEvents.length} event{filteredEvents.length !== 1 ? 's' : ''}
            </span>
          </div>
        </Card>
      )}

      {/* Content */}
      {events.length === 0 ? (
        <Card>
          <div className="text-center py-8 text-gray-500">
            <p className="text-lg font-medium mb-2">No race events found</p>
            <p className="text-sm">There are no events with type "Race" in the system.</p>
            {isAdmin && (
              <p className="text-sm mt-4 text-gray-600">
                To create race events, go to Events → Create Event and select "Race" as the event type.
              </p>
            )}
          </div>
        </Card>
      ) : filteredEvents.length === 0 ? (
        <Card>
          <div className="text-center py-8 text-gray-500">
            <p>No race events found for the selected season.</p>
            <p className="mt-2 text-sm">Try selecting "All Seasons" or a different season.</p>
          </div>
        </Card>
      ) : (
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Events List */}
          <div className="lg:col-span-1">
            <Card>
              <h2 className="text-lg font-semibold text-gray-900 mb-4">
                Events ({filteredEvents.length})
              </h2>
              <div className="space-y-2 max-h-[600px] overflow-y-auto">
                {filteredEvents.map(event => (
                  <button
                    key={event.event_id}
                    onClick={() => handleSelectEvent(event)}
                    className={`w-full text-left px-4 py-3 rounded-md transition-colors ${
                      selectedEventId === event.event_id
                        ? 'bg-primary-500 text-white'
                        : 'bg-gray-50 hover:bg-gray-100 text-gray-900'
                    }`}
                  >
                    <div className="font-medium">{event.event_name}</div>
                    <div className={`text-sm mt-1 ${
                      selectedEventId === event.event_id ? 'text-white' : 'text-gray-600'
                    }`}>
                      {format(new Date(event.event_starttime), 'MMM d, yyyy')}
                    </div>
                    {event.event_location && (
                      <div className={`text-xs mt-1 ${
                        selectedEventId === event.event_id ? 'text-white' : 'text-gray-500'
                      }`}>
                        {event.event_location_name || event.event_location}
                      </div>
                    )}
                  </button>
                ))}
              </div>
            </Card>
          </div>

          {/* Leaderboard */}
          <div className="lg:col-span-2">
            {selectedEvent ? (
              loadingResults ? (
                <Card>
                  <div className="flex justify-center items-center py-8">
                    <LoadingSpinner size="lg" />
                  </div>
                </Card>
              ) : (
                <RaceResultLeaderboard
                  results={raceResults}
                  riders={riders}
                  eventName={selectedEvent.event_name}
                  showRecordButton={isAdmin || isCoach}
                  onRecordClick={handleOpenRecordModal}
                  onEditClick={(isAdmin || isCoach) ? handleOpenEditModal : undefined}
                  onDeleteSuccess={handleRecordSuccess}
                />
              )
            ) : (
              <Card>
                <div className="text-center py-8 text-gray-500">
                  <p>Select an event to view results</p>
                </div>
              </Card>
            )}
          </div>
        </div>
      )}

      {/* Record/Edit Result Modal */}
      {selectedEvent && (
        <RaceResultModal
          isOpen={showRecordModal}
          onClose={handleCloseRecordModal}
          eventId={selectedEvent.event_id}
          eventName={selectedEvent.event_name}
          onSuccess={handleRecordSuccess}
          editMode={!!editingResult}
          existingResult={editingResult || undefined}
        />
      )}
    </div>
  );
}

export default RaceResults;

